import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  ColumnDef,
  ColumnFiltersState,
  ColumnSizingState,
  ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getGroupedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  GroupingState,
  OnChangeFn,
  PaginationState,
  RowSelectionState,
  SortingState,
  Updater,
  useReactTable,
  VisibilityState
} from '@tanstack/react-table'
import { ICSActionIcon } from '../ICSActionIcon/ICSActionIcon'
import { Icon, IconRef } from '../Icon'
import './TanStack.css'

export type TanStackDatagridSortDirection = 'asc' | 'desc'

export interface TanStackDatagridSort {
  columnId: string
  direction: TanStackDatagridSortDirection
}

export interface TanStackDatagridFilter {
  columnId: string
  value: string
}

export type TanStackDatagridGrouping = string[]

export interface TanStackDatagridPagination {
  pageIndex: number
  pageSize: number
  totalRows?: number
}

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100]

export interface TanStackDatagridColumn<TData> {
  id: string
  header: React.ReactNode
  accessor: (row: TData) => React.ReactNode
  cell?: (row: TData) => React.ReactNode
  groupable?: boolean
  groupCell?: (value: React.ReactNode, groupedRows: TData[]) => React.ReactNode
  sortable?: boolean
  filterable?: boolean
  resizable?: boolean
  hideable?: boolean
  width?: number
  minWidth?: number
  maxWidth?: number
  className?: string
  headerClassName?: string
}

export interface TanStackDatagridRowAction<TData> {
  id: string
  label: string
  icon: IconRef
  hidden?: (row: TData) => boolean
  disabled?: (row: TData) => boolean
  onClick: (row: TData) => void
}

export interface TanStackDatagridSelection<TData> {
  selectedRowIds: string[]
  onSelectionChange: (selectedRowIds: string[], selectedRows: TData[]) => void
}

export interface TanStackDatagridProps<TData> {
  data: TData[]
  columns: TanStackDatagridColumn<TData>[]
  getRowId: (row: TData) => string
  emptyMessage?: string
  loading?: boolean
  enablePagination?: boolean
  enableColumnSelection?: boolean
  toolbarContent?: React.ReactNode
  toolbarActions?: React.ReactNode
  className?: string
  testId?: string
  actions?: TanStackDatagridRowAction<TData>[]
  grouping?: TanStackDatagridGrouping
  onGroupingChange?: (grouping: TanStackDatagridGrouping) => void
  sort?: TanStackDatagridSort | null
  onSortChange?: (sort: TanStackDatagridSort | null) => void
  filters?: TanStackDatagridFilter[]
  onFilterChange?: (filters: TanStackDatagridFilter[]) => void
  manualFiltering?: boolean
  pagination?: TanStackDatagridPagination
  onPaginationChange?: (pagination: TanStackDatagridPagination) => void
  pageSizeOptions?: number[]
  selection?: TanStackDatagridSelection<TData>
  columnVisibility?: Record<string, boolean>
  onColumnVisibilityChange?: (visibility: Record<string, boolean>) => void
  onColumnResize?: (columnId: string, width: number) => void
}

type ColumnMeta<TData> = {
  className?: string
  headerClassName?: string
  headerLabel?: React.ReactNode
  groupCell?: (value: React.ReactNode, groupedRows: TData[]) => React.ReactNode
}

type FilterPopoverPosition = {
  left: number
  top: number
}

const resolveUpdater = <TValue,>(
  updater: Updater<TValue>,
  previousValue: TValue
): TValue => {
  return typeof updater === 'function'
    ? (updater as (value: TValue) => TValue)(previousValue)
    : updater
}

const createSortingState = (sort?: TanStackDatagridSort | null): SortingState => {
  return sort ? [{ id: sort.columnId, desc: sort.direction === 'desc' }] : []
}

const createFilterState = (
  filters?: TanStackDatagridFilter[]
): ColumnFiltersState => {
  return (filters || []).map((filter) => ({
    id: filter.columnId,
    value: filter.value
  }))
}

const createGroupingState = (grouping?: TanStackDatagridGrouping): GroupingState => {
  return grouping || []
}

const createRowSelectionState = <TData,>(
  selection?: TanStackDatagridSelection<TData>
): RowSelectionState => {
  return (selection?.selectedRowIds || []).reduce<RowSelectionState>(
    (selectedRows, rowId) => ({ ...selectedRows, [rowId]: true }),
    {}
  )
}

const TanStackDatagrid = <TData,>({
  data,
  columns,
  getRowId,
  emptyMessage = 'No data to display',
  loading = false,
  enablePagination = false,
  enableColumnSelection = true,
  toolbarContent,
  toolbarActions,
  className,
  testId,
  actions = [],
  grouping,
  onGroupingChange,
  sort,
  onSortChange,
  filters,
  onFilterChange,
  manualFiltering = false,
  pagination,
  onPaginationChange,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  selection,
  columnVisibility,
  onColumnVisibilityChange,
  onColumnResize
}: TanStackDatagridProps<TData>): React.ReactElement => {
  const [internalSort, setInternalSort] = useState<SortingState>([])
  const [internalFilters, setInternalFilters] = useState<ColumnFiltersState>([])
  const [internalGrouping, setInternalGrouping] = useState<GroupingState>([])
  const [internalPagination, setInternalPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10
  })
  const [internalExpanded, setInternalExpanded] = useState<ExpandedState>(true)
  const [pageInputValue, setPageInputValue] = useState('1')
  const [internalColumnVisibility, setInternalColumnVisibility] =
    useState<VisibilityState>({})
  const [internalColumnSizing, setInternalColumnSizing] =
    useState<ColumnSizingState>({})
  const [openFilterColumnId, setOpenFilterColumnId] = useState<string | null>(null)
  const [filterPopoverPosition, setFilterPopoverPosition] =
    useState<FilterPopoverPosition | null>(null)
  const filterAnchorRef = useRef<HTMLDivElement | null>(null)
  const filterPopoverRef = useRef<HTMLDivElement | null>(null)
  const columnMenuRef = useRef<HTMLDetailsElement | null>(null)

  const sortingState = sort !== undefined ? createSortingState(sort) : internalSort
  const filterState = filters !== undefined ? createFilterState(filters) : internalFilters
  const groupingState = grouping !== undefined ? createGroupingState(grouping) : internalGrouping
  const paginationState = pagination
    ? { pageIndex: pagination.pageIndex, pageSize: pagination.pageSize }
    : internalPagination
  const pageCount = pagination?.totalRows !== undefined
    ? Math.ceil(pagination.totalRows / paginationState.pageSize)
    : undefined
  const visibilityState = columnVisibility || internalColumnVisibility
  const rowSelectionState = createRowSelectionState(selection)

  const actionColumn = useMemo<ColumnDef<TData> | null>(() => {
    if (actions.length === 0) return null

    return {
      id: 'Action',
      header: 'Action',
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ row }) => (
        <div className="tsdg-actions">
          {actions
            .filter((action) => !action.hidden?.(row.original))
            .map((action) => {
              const disabled = action.disabled?.(row.original) || false
              return (
                <ICSActionIcon
                  key={action.id}
                  icon={action.icon}
                  title={action.label}
                  renderData={{ data: row.original, row: { data: row.original } }}
                  disabled={disabled}
                  onClick={(event) => {
                    event.preventDefault()
                    if (!disabled) action.onClick(row.original)
                  }}
                />
              )
            })}
        </div>
      )
    }
  }, [actions])

  const selectionColumn = useMemo<ColumnDef<TData> | null>(() => {
    if (!selection) return null

    return {
      id: 'selection',
      header: ({ table }) => (
        <input
          aria-label="Select all rows"
          type="checkbox"
          checked={table.getIsAllPageRowsSelected()}
          ref={(input) => {
            if (input) input.indeterminate = table.getIsSomePageRowsSelected()
          }}
          onChange={table.getToggleAllPageRowsSelectedHandler()}
        />
      ),
      enableSorting: false,
      enableColumnFilter: false,
      enableResizing: false,
      cell: ({ row }) => (
        <input
          aria-label="Select row"
          type="checkbox"
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          onChange={row.getToggleSelectedHandler()}
        />
      )
    }
  }, [selection])

  const tableColumns = useMemo<ColumnDef<TData>[]>(() => {
    const dataColumns = columns.map<ColumnDef<TData>>((column) => ({
      id: column.id,
      header: () => column.header,
      accessorFn: column.accessor,
      cell: ({ row, getValue }) =>
        column.cell ? column.cell(row.original) : String(getValue() ?? ''),
      enableGrouping: column.groupable || false,
      enableSorting: column.sortable || false,
      enableColumnFilter: column.filterable || false,
      enableResizing: column.resizable !== false,
      enableHiding: column.hideable !== false,
      size: column.width,
      minSize: column.minWidth,
      maxSize: column.maxWidth,
      meta: {
        className: column.className,
        headerClassName: column.headerClassName,
        headerLabel: column.header,
        groupCell: column.groupCell
      }
    }))

    return [selectionColumn, actionColumn, ...dataColumns].filter(
      (column): column is ColumnDef<TData> => Boolean(column)
    )
  }, [actionColumn, columns, selectionColumn])

  const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
    const nextSorting = resolveUpdater(updater, sortingState)
    const nextSort = nextSorting[0]

    if (onSortChange) {
      onSortChange(
        nextSort
          ? {
              columnId: nextSort.id,
              direction: nextSort.desc ? 'desc' : 'asc'
            }
          : null
      )
      return
    }

    setInternalSort(nextSorting)
  }

  const handleFilterChange: OnChangeFn<ColumnFiltersState> = (updater) => {
    const nextFilters = resolveUpdater(updater, filterState)

    if (onFilterChange) {
      onFilterChange(
        nextFilters.map((filter) => ({
          columnId: filter.id,
          value: String(filter.value ?? '')
        }))
      )
      return
    }

    setInternalFilters(nextFilters)
  }

  const handlePaginationChange: OnChangeFn<PaginationState> = (updater) => {
    const nextPagination = resolveUpdater(updater, paginationState)

    if (onPaginationChange) {
      onPaginationChange({
        ...pagination,
        pageIndex: nextPagination.pageIndex,
        pageSize: nextPagination.pageSize
      })
      return
    }

    setInternalPagination(nextPagination)
  }

  const handleGroupingChange: OnChangeFn<GroupingState> = (updater) => {
    const nextGrouping = resolveUpdater(updater, groupingState)

    if (onGroupingChange) {
      onGroupingChange(nextGrouping)
      return
    }

    setInternalGrouping(nextGrouping)
  }

  const handleVisibilityChange: OnChangeFn<VisibilityState> = (updater) => {
    const nextVisibility = resolveUpdater(updater, visibilityState)

    if (onColumnVisibilityChange) {
      onColumnVisibilityChange(nextVisibility)
      return
    }

    setInternalColumnVisibility(nextVisibility)
  }

  const handleRowSelectionChange: OnChangeFn<RowSelectionState> = (updater) => {
    if (!selection) return

    const nextSelection = resolveUpdater(updater, rowSelectionState)
    const selectedRowIds = Object.keys(nextSelection).filter(
      (rowId) => nextSelection[rowId]
    )
    const selectedRows = data.filter((row) => selectedRowIds.includes(getRowId(row)))
    selection.onSelectionChange(selectedRowIds, selectedRows)
  }

  const handleColumnSizingChange: OnChangeFn<ColumnSizingState> = (updater) => {
    const nextColumnSizing = resolveUpdater(updater, internalColumnSizing)
    setInternalColumnSizing(nextColumnSizing)

    if (onColumnResize) {
      Object.entries(nextColumnSizing).forEach(([columnId, width]) => {
        onColumnResize(columnId, Number(width))
        
      })
    }
  }

  const table = useReactTable({
    data,
    columns: tableColumns,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    columnResizeMode: 'onChange',
    enableRowSelection: Boolean(selection),
    autoResetPageIndex: false,
    manualFiltering,
    manualPagination: pagination?.totalRows !== undefined,
    pageCount,
    state: {
      grouping: groupingState,
      sorting: sortingState,
      columnFilters: filterState,
      pagination: paginationState,
      columnVisibility: visibilityState,
      rowSelection: rowSelectionState,
      columnSizing: internalColumnSizing,
      expanded: internalExpanded
    },
    onGroupingChange: handleGroupingChange,
    onSortingChange: handleSortingChange,
    onColumnFiltersChange: handleFilterChange,
    onPaginationChange: handlePaginationChange,
    onColumnVisibilityChange: handleVisibilityChange,
    onRowSelectionChange: handleRowSelectionChange,
    onColumnSizingChange: handleColumnSizingChange,
    onExpandedChange: setInternalExpanded
  })

  const currentPageNumber = table.getState().pagination.pageIndex + 1
  const totalPageCount = table.getPageCount() || 1

  const commitPageInputValue = () => {
    const nextPageNumber = Number(pageInputValue)

    if (!Number.isFinite(nextPageNumber)) {
      setPageInputValue(String(currentPageNumber))
      return
    }

    const clampedPageNumber = Math.min(
      Math.max(Math.trunc(nextPageNumber), 1),
      totalPageCount
    )

    table.setPageIndex(clampedPageNumber - 1)
    setPageInputValue(String(clampedPageNumber))
  }

  const handlePageInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      commitPageInputValue()
    }
  }

  useEffect(() => {
    setPageInputValue(String(currentPageNumber))
  }, [currentPageNumber])

  const visibleLeafColumns = table.getVisibleLeafColumns()
  const rows = enablePagination
    ? table.getRowModel().rows
    : table.getPrePaginationRowModel().rows
  const activeFilterHeader = useMemo(() => {
    if (!openFilterColumnId) return null

    return (
      table
        .getHeaderGroups()
        .flatMap((headerGroup) => headerGroup.headers)
        .find(
          (header) =>
            !header.isPlaceholder &&
            header.column.getCanFilter() &&
            header.column.id === openFilterColumnId
        ) || null
    )
  }, [openFilterColumnId, table])

  useLayoutEffect(() => {
    if (!openFilterColumnId || !filterAnchorRef.current) {
      setFilterPopoverPosition(null)
      return
    }

    const updateFilterPopoverPosition = () => {
      const anchorRect = filterAnchorRef.current?.getBoundingClientRect()

      if (!anchorRect) return

      setFilterPopoverPosition({
        left: anchorRect.right,
        top: anchorRect.bottom + 4
      })
    }

    updateFilterPopoverPosition()

    window.addEventListener('resize', updateFilterPopoverPosition)
    document.addEventListener('scroll', updateFilterPopoverPosition, true)

    return () => {
      window.removeEventListener('resize', updateFilterPopoverPosition)
      document.removeEventListener('scroll', updateFilterPopoverPosition, true)
    }
  }, [openFilterColumnId])

  useEffect(() => {
    if (!openFilterColumnId) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpenFilterColumnId(null)
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [openFilterColumnId])

  useEffect(() => {
    if (!openFilterColumnId) return

    const handlePointerDown = (event: MouseEvent) => {
      const targetNode = event.target as Node | null

      if (!targetNode) return

      if (filterPopoverRef.current?.contains(targetNode)) return
      if (filterAnchorRef.current?.contains(targetNode)) return

      setOpenFilterColumnId(null)
    }

    document.addEventListener('mousedown', handlePointerDown)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
    }
  }, [openFilterColumnId])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && columnMenuRef.current?.open) {
        columnMenuRef.current.open = false
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      const targetNode = event.target as Node | null

      if (!targetNode || !columnMenuRef.current?.open) return
      if (columnMenuRef.current.contains(targetNode)) return

      columnMenuRef.current.open = false
    }

    document.addEventListener('mousedown', handlePointerDown)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
    }
  }, [])

  const columnMenu = (
    <details className="tsdg-column-menu" ref={columnMenuRef}>
      <summary className="btn btn-sm btn-light tsdg-column-menu-button">Columns</summary>
      <div className="tsdg-column-menu-list border bg-white">
        {table
          .getAllLeafColumns()
          .filter((column) => column.getCanHide())
          .map((column) => (
            <label key={column.id} className="d-flex align-items-center mb-1">
              <input
                type="checkbox"
                className="mr-2"
                checked={column.getIsVisible()}
                onChange={column.getToggleVisibilityHandler()}
              />
              {String(
                (column.columnDef.meta as ColumnMeta<TData> | undefined)?.headerLabel ||
                  column.id
              )}
            </label>
          ))}
      </div>
    </details>
  )

  const filterPopover =
    activeFilterHeader && filterPopoverPosition && typeof document !== 'undefined'
      ? createPortal(
          <div
            className="tsdg-filter-popover tsdg-filter-popover-portal"
            ref={filterPopoverRef}
            style={{
              left: `${filterPopoverPosition.left}px`,
              top: `${filterPopoverPosition.top}px`,
              transform: 'translateX(-100%)'
            }}
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <input
              aria-label={`Filter ${activeFilterHeader.column.id}`}
              className="form-control form-control-sm tsdg-filter-input"
              placeholder="Filter"
              value={String(activeFilterHeader.column.getFilterValue() ?? '')}
              onChange={(event) =>
                activeFilterHeader.column.setFilterValue(event.target.value)
              }
            />
            <button
              type="button"
              className="tsdg-filter-clear"
              disabled={!activeFilterHeader.column.getFilterValue()}
              onClick={() => activeFilterHeader.column.setFilterValue('')}
            >
              Clear
            </button>
          </div>,
          document.body
        )
      : null

  return (
    <>
      <div
        className={['tsdg-container d-flex flex-column w-100', className]
          .filter(Boolean)
          .join(' ')}
        data-testid={testId}
      >
        {(toolbarContent || toolbarActions || enableColumnSelection) && (
          <div
            className="tsdg-toolbar d-flex align-items-center justify-content-between flex-wrap"
            data-testid="tsdg-toolbar"
          >
            <div className="tsdg-toolbar-left d-flex align-items-center">
              {toolbarContent}
            </div>
            <div className="tsdg-toolbar-right d-flex align-items-center ml-auto">
              {toolbarActions}
              {enableColumnSelection && columnMenu}
            </div>
          </div>
        )}

        <div
          className={[
            'tsdg-scroll flex-grow-1',
            'tsdg-scroll-no-responsive'
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <table className="table table-bordered table-striped table-hover mb-0 tsdg-table" role="grid">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const meta = header.column.columnDef.meta as ColumnMeta<TData> | undefined

                  return (
                    <th
                      key={header.id}
                      className={[
                        'align-middle position-relative px-3 py-2 bg-white text-dark',
                        'tsdg-header-cell',
                        meta?.headerClassName
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      colSpan={header.colSpan}
                      style={{ width: header.getSize() }}
                      onClick={
                        header.column.getCanSort()
                          ? header.column.getToggleSortingHandler()
                          : undefined
                      }
                    >
                      {header.isPlaceholder ? null : (
                        <div className="d-flex flex-column w-100">
                          <div className="tsdg-header-content">
                            <span className="flex-grow-1 text-truncate">
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                            </span>

                            {header.column.getCanSort() && (
                              <span
                                className={[
                                  'tsdg-sort-indicator ml-2',
                                  header.column.getIsSorted()
                                    ? 'tsdg-sort-indicator-active'
                                    : ''
                                ]
                                  .filter(Boolean)
                                  .join(' ')}
                                aria-hidden="true"
                              >
                                {header.column.getIsSorted() === 'asc' ? (
                                  <Icon use="lucide#arrow-up" />
                                ) : header.column.getIsSorted() === 'desc' ? (
                                  <Icon use="lucide#arrow-down" />
                                ) : (
                                  <Icon use="lucide#arrow-down-up" />
                                )}
                              </span>
                            )}

                            {header.column.getCanFilter() && (
                              <div
                                className="tsdg-filter-anchor"
                                ref={
                                  openFilterColumnId === header.column.id
                                    ? filterAnchorRef
                                    : null
                                }
                              >
                                <button
                                  type="button"
                                  className={[
                                    'tsdg-filter-button',
                                    header.column.getFilterValue()
                                      ? 'tsdg-filter-button-active'
                                      : ''
                                  ]
                                    .filter(Boolean)
                                    .join(' ')}
                                  aria-label={`Filter ${header.column.id}`}
                                  title={`Filter ${header.column.id}`}
                                  onClick={(event) => {
                                    event.stopPropagation()
                                    setOpenFilterColumnId((currentColumnId) =>
                                      currentColumnId === header.column.id
                                        ? null
                                        : header.column.id
                                    )
                                  }}
                                >
                                  <Icon
                                    use="lucide#funnel"
                                    className="icon-sm tsdg-filter-icon-svg"
                                  />
                                </button>
                              </div>
                            )}
                          </div>

                          {header.column.getCanResize() && (
                            <div
                              className={`tsdg-resizer ${
                                header.column.getIsResizing() ? 'isResizing' : ''
                              }`}
                              onMouseDown={header.getResizeHandler()}
                              onTouchStart={header.getResizeHandler()}
                              aria-hidden="true"
                            />
                          )}
                        </div>
                      )}
                    </th>
                  )
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className="text-center text-muted py-4" colSpan={visibleLeafColumns.length}>
                  Loading...
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td className="text-center text-muted py-4" colSpan={visibleLeafColumns.length}>
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                (() => {
                  const groupedCell = row
                    .getAllCells()
                    .find((cell) => cell.getIsGrouped())

                  if (groupedCell) {
                    const meta = groupedCell.column.columnDef.meta as ColumnMeta<TData> | undefined
                    const groupedRows = row.subRows
                      .map((subRow) => subRow.original)
                      .filter((groupedRow): groupedRow is TData => Boolean(groupedRow))
                    const groupedContent = meta?.groupCell
                      ? meta.groupCell(groupedCell.getValue() as React.ReactNode, groupedRows)
                      : `${String(groupedCell.getValue() ?? '')} (${row.subRows.length})`

                    return (
                      <tr key={row.id}>
                        <td
                          className="tsdg-cell text-truncate overflow-hidden"
                          colSpan={visibleLeafColumns.length}
                        >
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 mr-2"
                            onClick={row.getToggleExpandedHandler()}
                          >
                            {row.getIsExpanded() ? '▾' : '▸'}
                          </button>
                          {groupedContent}
                        </td>
                      </tr>
                    )
                  }

                  return (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell) => {
                        const meta = cell.column.columnDef.meta as ColumnMeta<TData> | undefined

                        return (
                          <td
                            key={cell.id}
                            className={[
                              'tsdg-cell text-truncate overflow-hidden',
                              meta?.className
                            ]
                              .filter(Boolean)
                              .join(' ')}
                            style={{ width: cell.column.getSize() }}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        )
                      })}
                    </tr>
                  )
                })()
              ))
            )}
          </tbody>
          </table>
        </div>

        { enablePagination && (
          <div
            className="tsdg-footer d-flex align-items-center justify-content-between flex-wrap"
            data-testid="tsdg-footer"
          >
            <div className="tsdg-footer-right d-flex align-items-center ml-auto">
              <button
                type="button"
                className="btn btn-sm btn-secondary mr-2"
                onClick={() => table.setPageIndex(0)}
                disabled={!table.getCanPreviousPage()}
              >
                First
              </button>
              <button
                type="button"
                className="btn btn-sm btn-secondary mr-2"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                Prev
              </button>
              <span className="mr-2 tsdg-page-label">
                Page
                <input
                  aria-label="Page number"
                  className="form-control form-control-sm tsdg-page-input"
                  max={totalPageCount}
                  min={1}
                  onBlur={commitPageInputValue}
                  onChange={(event) => setPageInputValue(event.target.value)}
                  onKeyDown={handlePageInputKeyDown}
                  type="number"
                  value={pageInputValue}
                />
                of {totalPageCount}
              </span>
              <button
                type="button"
                className="btn btn-sm btn-secondary mr-2"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                Next
              </button>
              <button
                type="button"
                className="btn btn-sm btn-secondary mr-2"
                onClick={() => table.setPageIndex(totalPageCount - 1)}
                disabled={!table.getCanNextPage()}
              >
                Last
              </button>
              <select
                className="form-control form-control-sm tsdg-page-size"
                value={table.getState().pagination.pageSize}
                onChange={(event) => table.setPageSize(Number(event.target.value))}
              >
                {pageSizeOptions.map((pageSize) => (
                  <option key={pageSize} value={pageSize}>
                    {pageSize}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {filterPopover}
    </>
  )
}

export default TanStackDatagrid
